<script setup lang="ts">
import { useSerialize } from "@lxc/app-device-common";
import type { Rules } from "async-validator";
import { ref } from "vue";
import { useAcl } from "vue-simple-acl";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import type { EntityLocation } from "~/core/models/EntityLocation.interface";
import LxcError from "~/core/models/LxcError.class";
import { getEntityLocationValidationRules } from "~/core/utils/entityLocationValidationRules";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import type { BusinessUnit } from "~/modules/businessUnit/models/BusinessUnit.interface";
import type { BusinessUnitUpdate } from "~/modules/businessUnit/models/BusinessUnitUpdate.type";
import businessUnitService from "~/modules/businessUnit/services/businessUnit.service";
import { useConfirmLeavePageStore } from "~/stores/useConfirmLeavePageStore";

interface Props {
  modelValue: BusinessUnit;
}
const props = defineProps<Props>();

const { t } = useI18n();
const { can } = useAcl();
const serialize = useSerialize();
const confirmLeavePageStore = useConfirmLeavePageStore();

const name: Ref<string> = ref(props.modelValue?.name);
const location: Ref<EntityLocation> = ref({
  country: props.modelValue?.country,
  timezone: props.modelValue?.timezone,
  latitude: props.modelValue?.latitude,
  longitude: props.modelValue?.longitude,
});
let initLocationFormStringified: Ref<string> = ref(serialize(location.value));
const formHasChanged: ComputedRef<boolean> = computed(
  () => serialize(location.value) !== initLocationFormStringified.value,
);
const isLoading: Ref<boolean> = ref(false);
const buttonSaveLabel: string = t("button.save");

const formRef: Ref<HTMLFormElement | undefined | null> = ref();
const formRules: Rules = getEntityLocationValidationRules(t);
const isFormValid: ComputedRef<boolean | undefined> = computed(
  () => formRef.value?.isValid,
);

const canUpdateForm = computed(() => can(ACLRoles.CAN_WRITE_ORG));

watch(
  () => formHasChanged.value,
  (hasChanged: boolean) => {
    confirmLeavePageStore.activate(hasChanged);
  },
);

async function onFormSubmit() {
  if (isFormValid.value) {
    isLoading.value = true;

    const businessUnitRequest: BusinessUnitUpdate = {
      ...location.value,
      name: name.value,
      code: props.modelValue?.code,
    };

    const response =
      await businessUnitService.updateBusinessUnit(businessUnitRequest);

    if (LxcError.check(response)) {
      response.notify(NotificationKey.saveError);
    } else {
      showNotificationSuccess(t(NotificationKey.saveSuccess));
      initLocationFormStringified.value = serialize(location.value);
    }

    isLoading.value = false;
  }
}
</script>

<template>
  <lxc-form
    ref="formRef"
    :rules="formRules"
    :model="location"
    @submit.prevent="onFormSubmit"
  >
    <h4 class="mb-6 text-gray-900">
      {{ t("administration.businessUnit.settings.description.information") }}
    </h4>
    <lxc-form-item
      :read-only="false"
      :label="t('administration.businessUnit.name')"
    >
      <lxc-input
        v-model="name"
        class="md:max-w-[calc(25%-32px)]"
        type="text"
        :disabled="true"
      />
    </lxc-form-item>
    <hr class="my-8 max-w-[75%] text-gray-200" />
    <entity-location
      v-model="location"
      :disabled="!canUpdateForm"
    ></entity-location>
    <lxc-button
      class="mt-4"
      type="primary"
      :is-loading="isLoading"
      :disabled="!formHasChanged || !isFormValid || !canUpdateForm"
      :title="buttonSaveLabel"
      :text="buttonSaveLabel"
      html-type="submit"
    />
  </lxc-form>
</template>
